import { CartesianGrid, Legend, Line, LineChart as RLineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Box from "@mui/material/Box";
import ChartLegend from "./ChartLegend";
import { SERIES_COLORS } from "./palette";
import { withPartial } from "./granularity/granularity";

export type LineDatum = { x: string; [serie: string]: number | string };
export type LineSeries = { key: string; label: string; color?: string; dashed?: boolean };
/** Destaque "daqui em diante": linha vertical tracejada em `x`, área até o último ponto e rótulo. */
export type LineHighlight = { x: string; label: string; color?: string };
export type LineChartProps = { data: LineDatum[]; series: LineSeries[]; ariaLabel: string; height?: number; width?: number; valueFormatter?: (v: number) => string; highlightFrom?: LineHighlight;
  /** Formato só dos ticks do eixo Y (padrão: `valueFormatter`); `yTicks` explícitos (domínio = 1º..último); `yAxisWidth` em px (padrão 60). */
  yTickFormatter?: (v: number) => string; yTicks?: number[]; yAxisWidth?: number;
  /** Legenda em HTML abaixo do gráfico + eixo X que não encavala rótulos (V32). `legendNote` vai no fim da legenda (ex.: "* projeção"). */
  htmlLegend?: boolean; legendNote?: string;
  /** Marca cada ponto: séries com vãos (bucket sem dado = null) não somem quando um ponto fica isolado. */
  dots?: boolean;
  /** Pontos (`x`) parciais: "(parcial)" no tooltip. */
  partialXs?: string[] };

export default function LineChart({ data, series, ariaLabel, height = 240, width, valueFormatter, yTickFormatter, yTicks, yAxisWidth, highlightFrom, htmlLegend, legendNote, dots, partialXs = [] }: LineChartProps) {
  const partial = new Set(partialXs);
  const lastX = data.length ? data[data.length - 1].x : undefined;
  const hl = highlightFrom?.color ?? "#26a69a";
  const chart = (
    <RLineChart data={data} width={width} height={height} margin={htmlLegend ? { top: 8, right: 32, bottom: 4, left: 8 } : { top: 5, right: 24, bottom: 5, left: 5 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="x" {...(htmlLegend ? { interval: "preserveStartEnd" as const, minTickGap: 16 } : {})} />
      <YAxis tickFormatter={yTickFormatter ?? valueFormatter} {...(yTicks?.length ? { ticks: yTicks, domain: [yTicks[0], yTicks[yTicks.length - 1]] } : {})}
        {...(yAxisWidth ? { width: yAxisWidth } : {})} />
      <Tooltip labelFormatter={(x: string) => withPartial(String(x), partial.has(x))} formatter={valueFormatter ? (v: number) => valueFormatter(v) : undefined} />
      {!htmlLegend && <Legend />}
      {highlightFrom && lastX && <ReferenceArea x1={highlightFrom.x} x2={lastX} fill={hl} fillOpacity={0.07} />}
      {highlightFrom && (
        <ReferenceLine x={highlightFrom.x} stroke={hl} strokeWidth={1.5} strokeDasharray="4 3"
          label={{ value: highlightFrom.label, position: "insideBottomRight", fill: "#1b5e20", fontSize: 11, fontWeight: 700 }} />
      )}
      {series.map((s, i) => (
        <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color ?? SERIES_COLORS[i % SERIES_COLORS.length]} strokeDasharray={s.dashed ? "5 4" : undefined} dot={dots ? { r: 3 } : false} isAnimationActive={false} />
      ))}
    </RLineChart>
  );
  return (
    <Box role="img" aria-label={ariaLabel}>
      {width ? chart : <ResponsiveContainer width="100%" height={height}>{chart}</ResponsiveContainer>}
      {htmlLegend && (
        <ChartLegend note={legendNote}
          items={series.map((s, i) => ({ label: s.label, color: s.color ?? SERIES_COLORS[i % SERIES_COLORS.length], dashed: s.dashed }))} />
      )}
    </Box>
  );
}
